import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  followUserUseCase,
  unfollowUserUseCase,
  sendFriendRequestUseCase,
  friendshipRepository,
  followRepository,
} from '@/src/core/dependencies';
import { SearchUsersUseCase } from '@/src/domain/usecases/SearchUsersUseCase';
import { User } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { SearchUsersScreen } from '@/src/presentation/screens/SearchUsersScreen';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { logger } from '@/src/core/utils';
import { platformAlert } from '@/src/core/utils/platformAlert';

const PAGE_SIZE = 20;

// Initialize use case
const searchUsersUseCase = new SearchUsersUseCase();

export default function SearchUsersContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  // Track relationship status
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [friendIds, setFriendIds] = useState<Set<string>>(new Set());
  const [pendingSentIds, setPendingSentIds] = useState<Set<string>>(new Set());

  const handleSearch = useCallback(
    async (query: string) => {
      if (!user || query.trim().length < 2) {
        setResults([]);
        setHasMore(false);
        return;
      }

      try {
        setLoading(true);
        setPage(0);
        setHasMore(true);

        // Execute search
        const users = await searchUsersUseCase.execute(query, PAGE_SIZE, 0);

        // Filter out current user
        const filtered = users.filter(u => u.id !== user.id);
        setResults(filtered);
        setHasMore(users.length === PAGE_SIZE);

        // Load relationship status for each user
        await loadRelationshipStatus(filtered);
      } catch (error) {
        logger.error('SearchUsers', 'Search failed', { error });
        platformAlert(t('common.error'), t('errors.failedToLoad'));
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const loadRelationshipStatus = async (users: User[]) => {
    if (!user) return;

    const newFollowingIds = new Set<string>();
    const newFriendIds = new Set<string>();
    const newPendingSentIds = new Set<string>();

    await Promise.all(
      users.map(async (searchUser) => {
        try {
          // Check if following
          const isFollowing = await followRepository.isFollowing(user.id, searchUser.id);
          if (isFollowing) {
            newFollowingIds.add(searchUser.id);
          }

          // Check friendship status
          const areFriends = await friendshipRepository.areFriends(user.id, searchUser.id);
          if (areFriends) {
            newFriendIds.add(searchUser.id);
          } else {
            const pendingRequest = await friendshipRepository.getPendingRequestBetween(user.id, searchUser.id);
            if (pendingRequest && pendingRequest.requesterId === user.id) {
              newPendingSentIds.add(searchUser.id);
            }
          }
        } catch (error) {
          logger.error('SearchUsers', 'Failed to check relationship status', { error });
        }
      })
    );

    setFollowingIds(newFollowingIds);
    setFriendIds(newFriendIds);
    setPendingSentIds(newPendingSentIds);
  };

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !user || searchQuery.trim().length < 2) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const users = await searchUsersUseCase.execute(searchQuery, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (users.length > 0) {
        // Filter out current user
        const filtered = users.filter(u => u.id !== user.id);
        setResults((prev) => [...prev, ...filtered]);
        setPage(nextPage);
        setHasMore(users.length === PAGE_SIZE);

        // Load relationship status for new users
        await loadRelationshipStatus(filtered);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('SearchUsers', 'Failed to load more results', { error });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, user, searchQuery, page]);

  const handleFollowToggle = async (userId: string, isFollowing: boolean) => {
    if (!user) return;

    try {
      if (isFollowing) {
        await unfollowUserUseCase.execute(user.id, userId);
        setFollowingIds(prev => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      } else {
        await followUserUseCase.execute(user.id, userId);
        setFollowingIds(prev => new Set(prev).add(userId));
      }
    } catch (error) {
      logger.error('SearchUsers', 'Failed to toggle follow', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleAddFriend = async (userId: string) => {
    if (!user) return;

    try {
      await sendFriendRequestUseCase.execute(user.id, userId);
      setPendingSentIds(prev => new Set(prev).add(userId));
      platformAlert(t('common.success'), t('social.requestSent'));
    } catch (error) {
      logger.error('SearchUsers', 'Failed to add friend', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      handleSearch(query);
    } else if (query.length === 0) {
      setResults([]);
      setHasMore(false);
    }
  };

  return (
    <SearchUsersScreen
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      onSearch={handleSearch}
      results={results}
      loading={loading}
      loadingMore={loadingMore}
      currentUserId={user?.id}
      followingIds={followingIds}
      friendIds={friendIds}
      pendingSentIds={pendingSentIds}
      onLoadMore={handleLoadMore}
      onFollowToggle={handleFollowToggle}
      onAddFriend={handleAddFriend}
      onUserPress={handleUserPress}
      colors={colors}
    />
  );
}
